// Pure logic for the team avatar directory — extracted from the React
// provider so it can be unit-tested without pulling in React or the API
// client. The .tsx provider imports from here.
//
// The input shape is declared locally (rather than importing TeamAvatar
// from ../api) so this module has ZERO runtime/type dependency on the API
// client — that keeps it cheap to unit-test and avoids dragging the web
// app's import.meta.env types into the worker tsconfig's test compile.

export interface AvatarDirectoryEntry {
  user_id: string;
  name: string | null;
  image: string | null;
}

export interface AvatarLookup {
  /** Returns the raw users.image value for a teammate, or null. Prefers
   *  userId; falls back to a unique normalized name. */
  resolve: (opts: {
    userId?: string | null | undefined;
    name?: string | null | undefined;
  }) => string | null;
}

export function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

export function buildAvatarLookup(
  avatars: AvatarDirectoryEntry[],
): AvatarLookup {
  const byId = new Map<string, string>();
  // Track name → image, plus whether a name is ambiguous (maps to 2+
  // distinct people). Ambiguous names are dropped from the name index.
  const byName = new Map<string, string>();
  const nameSeenUserIds = new Map<string, Set<string>>();

  for (const a of avatars) {
    if (a.image) byId.set(a.user_id, a.image);
    if (a.name) {
      const key = normalizeName(a.name);
      if (!key) continue;
      let ids = nameSeenUserIds.get(key);
      if (!ids) {
        ids = new Set();
        nameSeenUserIds.set(key, ids);
      }
      ids.add(a.user_id);
      if (a.image) byName.set(key, a.image);
    }
  }
  // Drop any name that resolves to more than one person — can't trust it.
  for (const [key, ids] of nameSeenUserIds) {
    if (ids.size > 1) byName.delete(key);
  }

  return {
    resolve: ({ userId, name }) => {
      if (userId) {
        const byIdHit = byId.get(userId);
        if (byIdHit) return byIdHit;
      }
      if (name) {
        const byNameHit = byName.get(normalizeName(name));
        if (byNameHit) return byNameHit;
      }
      return null;
    },
  };
}

export const EMPTY_LOOKUP: AvatarLookup = { resolve: () => null };
